import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { trackPageView } from "@/lib/analytics";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import MobileBottomNav from "@/components/MobileBottomNav";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import MatchAlert from "@/components/MatchAlert";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Categories from "@/pages/Categories";
import ListingDetail from "@/pages/ListingDetail";
import PostTrade from "@/pages/PostTrade";
import Messages from "@/pages/Messages";
import MyTrades from "@/pages/MyTrades";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import SwipeDiscover from "@/pages/SwipeDiscover";
import SwipesInterested from "@/pages/SwipesInterested";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";

// Code-split: secondary + admin pages load on demand (smaller main bundle)
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Safety = lazy(() => import("@/pages/Safety"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const CardGameHub = lazy(() => import("@/pages/CardGameHub"));
const VersusPage = lazy(() => import("@/pages/VersusPage"));
const GeoCategoryPage = lazy(() => import("@/pages/GeoCategoryPage"));
const WhatCanIGet = lazy(() => import("@/pages/WhatCanIGet"));
const TradeCircleDetail = lazy(() => import("@/pages/TradeCircleDetail"));
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminListings = lazy(() => import("@/pages/admin/AdminListings"));
const AdminTrades = lazy(() => import("@/pages/admin/AdminTrades"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminErrorLogs = lazy(() => import("@/pages/admin/AdminErrorLogs"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AdminAuditLog"));

const PageSpinner = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-4 border-zinc-200 border-t-[#0654BA] animate-spin" />
  </div>
);

function AppShell() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  // CRITICAL: Handle Emergent OAuth session_id in URL fragment first
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="App min-h-screen bg-[#F7F7F7]">
      <Navbar />
      <main className="pb-24 md:pb-12">
        <ErrorBoundary>
          <Suspense fallback={<PageSpinner />}>
            <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/discover" element={<ProtectedRoute><SwipeDiscover /></ProtectedRoute>} />
          <Route path="/what-can-i-get" element={<ProtectedRoute><WhatCanIGet /></ProtectedRoute>} />
          <Route path="/trade-circles/:id" element={<ProtectedRoute><TradeCircleDetail /></ProtectedRoute>} />
          <Route path="/swipes/interested" element={<ProtectedRoute><SwipesInterested /></ProtectedRoute>} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/trade/cards/:game" element={<CardGameHub />} />
          <Route path="/vs/:competitor" element={<VersusPage />} />
          <Route path="/browse/:category/:city" element={<GeoCategoryPage />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {/* Protected */}
          <Route path="/post" element={<ProtectedRoute><PostTrade /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/:id" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/trades" element={<ProtectedRoute><MyTrades /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/me" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          {/* Admin */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="listings" element={<AdminListings />} />
            <Route path="trades" element={<AdminTrades />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="errors" element={<AdminErrorLogs />} />
            <Route path="audit" element={<AdminAuditLog />} />
          </Route>
        </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <MobileBottomNav />
      <MatchAlert />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
